import { create } from "zustand";
import { reviewApi } from "../api/review";

interface LikeState {
  reviewId: string;
  isLiked: boolean;
  likeCount: number;
}

interface LikeStore {
  likes: Map<string, LikeState>; // reviewId -> LikeState
  processingIds: Set<string>; // 처리 중인 리뷰 ID들
  /**
   * 이번 세션에서 사용자가 직접 눌러 바꾼 리뷰 ID들.
   *
   * 왜 필요한가
   * ----------
   * 홈 피드는 목록을 sessionStorage에 스냅샷으로 저장해 두고, 다른 화면에
   * 다녀오면 그걸로 복원한다. 그런데 스냅샷은 좋아요를 눌러도 갱신되지 않는다.
   * 그래서 "좋아요 → 다른 화면 → 홈으로 복귀" 하면 목록이 예전 값(안 누름)으로
   * 되살아나고, 그 값이 syncFromServer로 흘러들어 방금 누른 좋아요를 지웠다.
   *
   * 실제로 그랬다. 한 사용자가 리뷰 368에 2초 안에 네 번을 눌렀는데(하트가 계속
   * 되돌아가니 다시 누른 것), 서버 toggle은 누를 때마다 뒤집으므로 짝수 번이 되어
   * DB에는 좋아요가 하나도 남지 않았다.
   *
   * 사용자가 건드린 값은 이 세션에서 가장 최신이다. 스냅샷 같은 과거 데이터가
   * 그걸 덮어쓰지 못하게 막는다. (앱을 다시 켜면 비워져 서버 값으로 다시 채워진다)
   */
  dirtyIds: Set<string>;
  /**
   * @param fallback 스토어에 값이 아직 없을 때 쓸 현재 표시값.
   *   없으면 낙관적 업데이트가 통째로 건너뛰어져 "눌러도 아무 반응 없는" 것처럼 보인다.
   */
  toggleLike: (
    reviewId: string,
    fallback?: { isLiked: boolean; likeCount: number }
  ) => Promise<void>;
  getLikeState: (reviewId: string) => LikeState | null;
  setLikeState: (reviewId: string, isLiked: boolean, likeCount: number) => void;
  /**
   * 서버에서 새로 받아온 값으로 덮어쓴다.
   *
   * setLikeState와 달리 "이미 값이 있으면 두는" 게 아니라 항상 갱신한다.
   * 좋아요 수는 남이 바꾸는 값이라, 한 번 캐시해두고 계속 쓰면 다른 사람이
   * 누른 좋아요가 영영 안 보인다. 단 내가 누른 요청이 아직 처리 중이면
   * 그 값이 더 최신이므로 건드리지 않는다.
   */
  syncFromServer: (
    reviewId: string,
    isLiked: boolean,
    likeCount: number
  ) => void;
  isProcessing: (reviewId: string) => boolean;
}

export const useLikeStore = create<LikeStore>((set, get) => ({
  likes: new Map(),
  processingIds: new Set(),
  dirtyIds: new Set(),

  toggleLike: async (
    reviewId: string,
    fallback?: { isLiked: boolean; likeCount: number }
  ) => {
    const { processingIds, likes, dirtyIds } = get();

    // 이미 처리 중이면 중복 호출 방지
    if (processingIds.has(reviewId)) {
      return;
    }

    // 현재 상태(롤백/낙관적 업데이트용). 서버 toggle은 DB 상태를 무조건 뒤집는
    // blind flip이라, 표시 상태를 서버와 일치시켜 두지 않으면 "한 번은 헛클릭"이 생김.
    //
    // 스토어에 값이 없으면 화면이 보여주고 있던 값(fallback)을 쓴다. 예전엔 이때
    // 낙관적 업데이트를 통째로 건너뛰어, 눌러도 하트가 그대로라 안 눌린 줄 알고
    // 또 누르게 됐다.
    const current = likes.get(reviewId) ?? fallbackState(reviewId, fallback);

    // 낙관적 업데이트: 즉시 하트/카운트 반영
    const optimistic = new Map(likes);
    if (current) {
      optimistic.set(reviewId, {
        reviewId,
        isLiked: !current.isLiked,
        likeCount: current.isLiked
          ? Math.max(0, current.likeCount - 1)
          : current.likeCount + 1,
      });
    }
    set({
      likes: optimistic,
      processingIds: new Set(processingIds).add(reviewId),
      // 이제부터 이 리뷰는 사용자가 정한 값이 우선이다(과거 스냅샷이 못 덮게)
      dirtyIds: new Set(dirtyIds).add(reviewId),
    });

    try {
      const response = await reviewApi.toggleLike(parseInt(reviewId));

      // 서버 응답으로 확정 (낙관적 값 보정)
      const confirmed = new Map(get().likes);
      confirmed.set(reviewId, {
        reviewId,
        isLiked: response.is_liked,
        likeCount: response.like_count,
      });
      set({ likes: confirmed });
    } catch (error) {
      // 실패 시 이전 상태로 롤백
      if (current) {
        const rolled = new Map(get().likes);
        rolled.set(reviewId, current);
        set({ likes: rolled });
      }
      console.error("좋아요 처리 실패:", error);
      throw error;
    } finally {
      // 처리 완료 후 상태 제거
      const newProcessingIds = new Set(get().processingIds);
      newProcessingIds.delete(reviewId);
      set({
        processingIds: newProcessingIds,
      });
    }
  },

  getLikeState: (reviewId: string) => {
    return get().likes.get(reviewId) || null;
  },

  setLikeState: (reviewId: string, isLiked: boolean, likeCount: number) => {
    const { likes } = get();
    const newLikes = new Map(likes);
    newLikes.set(reviewId, {
      reviewId,
      isLiked,
      likeCount,
    });
    set({ likes: newLikes });
  },

  syncFromServer: (reviewId: string, isLiked: boolean, likeCount: number) => {
    const { likes, processingIds, dirtyIds } = get();
    // 내 토글이 아직 서버 응답을 기다리는 중이면 그 값이 더 최신이다.
    if (processingIds.has(reviewId)) return;
    // 내가 이미 누른 리뷰는 내 값이 최신이다. 홈 스냅샷처럼 눌리기 전에 찍힌
    // 목록이 되살아나 방금 누른 좋아요를 지우던 걸 여기서 막는다.
    if (dirtyIds.has(reviewId)) return;

    const current = likes.get(reviewId);
    if (
      current &&
      current.isLiked === isLiked &&
      current.likeCount === likeCount
    ) {
      return; // 값이 같으면 새 Map을 만들지 않는다(불필요한 리렌더 방지)
    }

    const next = new Map(likes);
    next.set(reviewId, { reviewId, isLiked, likeCount });
    set({ likes: next });
  },

  isProcessing: (reviewId: string) => {
    return get().processingIds.has(reviewId);
  },
}));

/** 스토어에 값이 없을 때 화면이 보여주던 값을 상태로 만들어 준다 */
function fallbackState(
  reviewId: string,
  fallback?: { isLiked: boolean; likeCount: number }
): LikeState | undefined {
  if (!fallback) return undefined;
  return {
    reviewId,
    isLiked: fallback.isLiked,
    likeCount: fallback.likeCount,
  };
}
